import React, {useMemo, useState} from 'react';
import {FormikHelpers} from 'formik';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {NavLink} from 'react-router-dom';
import {Breadcrumbs} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {DateRangePickerInput} from '~/components/Controls/DatePicker';
import {Input} from '~/components/Controls/Input';
import {SelectInput} from '~/components/Controls/Select/SelectInput';
import {ToggleGroup} from '~/components/Controls/ToggleGroup';
import {useConfirm, useMessage} from '~/components/Dialogs';
import {onSubmitError} from '~/components/Form';
import {useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {TableRowActions, TableSort, TableSortDirection} from '~/components/Table';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {Copy} from '~/lib/helpers';
import {Intent} from '~/lib/intent';
import {sortByNumber, sortByString} from '~/lib/sort';
import {deleteBusinessIncentive, updateBusinessIncentive, createBusinessIncentive} from '~/modules/BusinessIncentives/api';
import {BusinessIncentivesAbility} from '~/modules/BusinessIncentives/ability';
import {BusinessIncentivesNavigation} from '~/modules/BusinessIncentives/components/BusinessIncentivesNavigation';
import {
    BusinessIncentiveForm,
    IBusinessIncentiveFormProps,
    IBusinessIncentiveFormValues
} from '~/modules/BusinessIncentives/forms/BusinessIncentiveForm';
import {
    IBusinessIncentiveCreateRequest,
    IBusinessIncentive,
    IBusinessIncentiveUpdateRequest,
    incentiveStateLabel,
    IncentiveState,
    incentiveStructureLabel,
    IncentiveStructure,
    IncentiveType,
    incentiveTypeLabel,
    structureRequiresTier,
    structureRequiresValue
} from '~/modules/BusinessIncentives/models/BusinessIncentive';
import {businessIncentivesLinks} from '~/modules/BusinessIncentives/links';
import {
    BUSINESS_INCENTIVES_QUERY_KEY,
    useBusinessIncentivesQuery,
} from '~/modules/BusinessIncentives/queries';
import {hasStartedPeriod} from '~/modules/BusinessIncentives/utils/period';
import {toDisplayNumber, toDisplayText} from '~/modules/BusinessIncentives/utils/value';
import style from '~/modules/BusinessIncentives/views/index.module.scss';

interface IBusinessIncentivesFilter {
    search: string;
    state: IncentiveState | '';
    incentiveType: IncentiveType | '';
    structure: IncentiveStructure | '';
    periodStart: string;
    periodEnd: string;
}

const getErrorMessage = (error: unknown): string => {
    const value = error as {
        response?: {data?: unknown};
        message?: string;
    };
    const responseData = value?.response?.data;
    if (typeof responseData === 'string') {
        return responseData;
    }
    if (responseData && typeof responseData === 'object' && 'message' in responseData) {
        const responseMessage = (responseData as {message?: unknown}).message;
        if (typeof responseMessage === 'string' && responseMessage.trim()) {
            return responseMessage;
        }
    }
    return value?.message ?? 'Unknown error';
};

const formatScopePreview = (scope: IBusinessIncentive['scope']): string => toDisplayText(scope);

const formatNameMeta = (description: unknown): string => toDisplayText(description);

const formatTierUsd = (value: number): string => `$${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
}).format(value)}`;

const formatTierPercent = (value: number): string => `${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
}).format(value)}%`;

interface ITierDisplayRow {
    key: string;
    label: string;
    active: boolean;
}

const formatTierName = (value: string): string => {
    const normalized = value.trim();
    if (!normalized) {
        return '-';
    }
    if (normalized === 'not_applicable') {
        return 'Not applicable';
    }
    if (normalized === 'miss') {
        return 'Miss';
    }
    if (/^tier_\d+$/i.test(normalized)) {
        return `Tier ${normalized.split('_')[1]}`;
    }
    return normalized;
};

const getTierRows = (item: IBusinessIncentive): ITierDisplayRow[] => {
    const currentTier = toDisplayText(item.currentTier, '').trim();

    if (!structureRequiresTier(item.structure) || !Array.isArray(item.tiers) || item.tiers.length === 0) {
        if (currentTier && currentTier !== 'not_applicable') {
            return [{
                key: 'current',
                label: formatTierName(currentTier),
                active: true,
            }];
        }
        if (typeof item.value === 'number' && Number.isFinite(item.value)) {
            const isPercent = item.valueUnit === 'PERCENT';
            return [{
                key: 'value',
                label: isPercent ? formatTierPercent(item.value) : formatTierUsd(item.value),
                active: true,
            }];
        }
        return [{
            key: 'empty',
            label: '-',
            active: false,
        }];
    }

    const rows: ITierDisplayRow[] = item.tiers.map((tier, index) => {
        const tierName = tier.name.trim();
        return {
            key: `${tierName || 'tier'}-${index}`,
            label: `${formatTierName(tierName)} · ${formatTierUsd(tier.thresholdUsd)} · ${formatTierPercent(tier.percentage)}`,
            active: false,
        };
    });

    if (currentTier) {
        const activeIndex = item.tiers.findIndex(tier => tier.name.trim() === currentTier);
        if (activeIndex >= 0 && rows[activeIndex]) {
            rows[activeIndex] = {
                ...rows[activeIndex],
                active: true,
            };
            return rows;
        }
        rows.unshift({
            key: 'current',
            label: formatTierName(currentTier),
            active: true,
        });
        return rows;
    }

    if (rows[0]) {
        rows[0] = {
            ...rows[0],
            active: true,
        };
    }

    return rows;
};

const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

const parseIsoDate = (value: string): Date | undefined => {
    if (!dateRegex.test(value)) {
        return undefined;
    }
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
        return undefined;
    }
    return parsed;
};

const toIsoDate = (date?: Date): string => {
    if (!date || Number.isNaN(date.getTime())) {
        return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const normalizeDateRangeFilter = (startDate: string, endDate: string): {startDate: string; endDate: string} => {
    if (!dateRegex.test(startDate) || !dateRegex.test(endDate)) {
        return {startDate, endDate};
    }
    if (startDate <= endDate) {
        return {startDate, endDate};
    }
    return {startDate: endDate, endDate: startDate};
};

const matchesPeriodFilter = (periodStart: string, periodEnd: string, filterStart: string, filterEnd: string): boolean => {
    const hasFilterStart = dateRegex.test(filterStart);
    const hasFilterEnd = dateRegex.test(filterEnd);
    if (!hasFilterStart && !hasFilterEnd) {
        return true;
    }

    if (!dateRegex.test(periodStart) || !dateRegex.test(periodEnd) || periodStart > periodEnd) {
        return false;
    }

    const normalizedFilter = normalizeDateRangeFilter(filterStart, filterEnd);
    const normalizedFilterStart = dateRegex.test(normalizedFilter.startDate) ? normalizedFilter.startDate : '';
    const normalizedFilterEnd = dateRegex.test(normalizedFilter.endDate) ? normalizedFilter.endDate : '';

    if (normalizedFilterStart && periodEnd < normalizedFilterStart) {
        return false;
    }
    if (normalizedFilterEnd && periodStart > normalizedFilterEnd) {
        return false;
    }
    return true;
};

const toUpdatePayload = (values: IBusinessIncentiveFormValues): IBusinessIncentiveUpdateRequest => {
    const isTierStructure = structureRequiresTier(values.structure);
    const requiresValue = structureRequiresValue(values.structure);
    const monetaryLocked = hasStartedPeriod(values.startDate);
    const tiers = isTierStructure
        ? values.tiers.map((tier, index) => ({
            name: tier.name.trim(),
            thresholdUsd: index === 0 ? 0 : (Number(tier.thresholdUsd) || 0),
            percentage: Number(tier.percentage) || 0,
        }))
        : undefined;

    const normalizedCurrentTier = isTierStructure
        ? (values.currentTier.trim() || null)
        : undefined;

    const payload: IBusinessIncentiveUpdateRequest = {
        name: values.name.trim(),
        description: values.description.trim(),
        scope: values.scope.trim(),
        state: values.state,
        currentTier: normalizedCurrentTier,
        tiers,
        value: requiresValue && !monetaryLocked
            ? (values.value === '' ? null : Number(values.value))
            : undefined,
        endDate: values.endDate || undefined,
    };

    return payload;
};

const toCreatePayload = (values: IBusinessIncentiveFormValues): IBusinessIncentiveCreateRequest => {
    const isTierStructure = structureRequiresTier(values.structure);
    const requiresValue = structureRequiresValue(values.structure);
    const tiers = isTierStructure
        ? values.tiers.map((tier, index) => ({
            name: tier.name.trim(),
            thresholdUsd: index === 0 ? 0 : (Number(tier.thresholdUsd) || 0),
            percentage: Number(tier.percentage) || 0,
        }))
        : undefined;

    const normalizedCurrentTier = isTierStructure
        ? (values.currentTier.trim() || null)
        : undefined;

    const payload: IBusinessIncentiveCreateRequest = {
        incentiveType: values.incentiveType,
        structure: values.structure,
        scope: values.scope.trim(),
        name: values.name.trim(),
        description: values.description.trim(),
        startDate: values.startDate,
        endDate: values.endDate,
        state: values.state,
        currentTier: normalizedCurrentTier,
        tiers,
        value: requiresValue ? (values.value === '' ? null : Number(values.value)) : undefined,
    };

    return payload;
};

export const BusinessIncentivesView = () => {
    const ability = useAbility(BusinessIncentivesAbility);
    const modals = useModals();
    const confirm = useConfirm();
    const message = useMessage();
    const queryClient = useQueryClient();

    const [filter, setFilter] = useState<IBusinessIncentivesFilter>({
        search: '',
        state: '',
        incentiveType: '',
        structure: '',
        periodStart: '',
        periodEnd: '',
    });
    const incentivesQuery = useBusinessIncentivesQuery();
    const incentives = incentivesQuery.data ?? [];

    const filteredIncentives = useMemo(() => {
        const query = filter.search.trim().toLowerCase();
        return incentives.filter(item => {
            if (filter.state && toDisplayText(item.state, '') !== filter.state) {
                return false;
            }
            if (filter.incentiveType && toDisplayText(item.incentiveType, '') !== filter.incentiveType) {
                return false;
            }
            if (filter.structure && toDisplayText(item.structure, '') !== filter.structure) {
                return false;
            }
            if (!matchesPeriodFilter(
                toDisplayText(item.startDate, ''),
                toDisplayText(item.endDate, ''),
                filter.periodStart,
                filter.periodEnd
            )) {
                return false;
            }
            if (!query) {
                return true;
            }
            const haystack = [
                toDisplayText(item.id, ''),
                toDisplayText(item.name, ''),
                toDisplayText(item.description, ''),
                toDisplayText(item.incentiveType, ''),
                toDisplayText(item.currentTier, ''),
                formatScopePreview(item.scope),
            ]
                .join(' ')
                .toLowerCase();
            return haystack.includes(query);
        });
    }, [filter.search, filter.state, filter.incentiveType, filter.structure, filter.periodStart, filter.periodEnd, incentives]);

    const createMutation = useMutation({
        mutationFn: (payload: IBusinessIncentiveCreateRequest) => createBusinessIncentive(payload),
    });

    const updateMutation = useMutation({
        mutationFn: ({id, payload}: {id: number; payload: IBusinessIncentiveUpdateRequest}) =>
            updateBusinessIncentive(id, payload),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => deleteBusinessIncentive(id),
    });

    const isBusy =
        incentivesQuery.isFetching ||
        createMutation.isPending ||
        updateMutation.isPending ||
        deleteMutation.isPending;

    const invalidateAll = async () => {
        await queryClient.invalidateQueries({queryKey: BUSINESS_INCENTIVES_QUERY_KEY});
    };

    const formSubmit = (
        values: IBusinessIncentiveFormValues,
        formikActions: FormikHelpers<IBusinessIncentiveFormValues>,
        close: () => void
    ) => {
        if (values.id) {
            updateMutation.mutateAsync({
                id: values.id,
                payload: toUpdatePayload(values),
            })
                .then(async () => {
                    await invalidateAll();
                    message('Expense saved successfully.', {
                        intent: Intent.SUCCESS,
                        icon: <i className='fa fa-check'/>,
                    });
                    close();
                })
                .catch(onSubmitError(formikActions));
            return;
        }

        createMutation.mutateAsync(toCreatePayload(values))
            .then(async () => {
                await invalidateAll();
                close();
            })
            .catch(onSubmitError(formikActions));
    };

    const createIncentiveModal = modals.create<IBusinessIncentiveFormProps>(BusinessIncentiveForm, {
        onSubmit: formSubmit,
    });

    const editIncentiveModal = modals.create<IBusinessIncentiveFormProps>(BusinessIncentiveForm, {
        onSubmit: formSubmit,
    });

    const handleDelete = (incentive: IBusinessIncentive) => {
        if (typeof incentive.id !== 'number') {
            return;
        }
        confirm(`Delete expense "${toDisplayText(incentive.name)}"?`, {
            intent: Intent.DANGER,
            icon: <i className='fa fa-trash'/>,
            onOk: () => {
                deleteMutation.mutate(incentive.id as number, {
                    onSuccess: async () => {
                        await invalidateAll();
                        message('Expense deleted.', {
                            intent: Intent.SUCCESS,
                            icon: <i className='fa fa-check'/>,
                        });
                    },
                    onError: error => {
                        message(getErrorMessage(error), {
                            intent: Intent.DANGER,
                            icon: <i className='fa fa-times'/>,
                        });
                    }
                });
            },
        });
    };

    const stateToggleItems = useMemo(() => ([
        {label: 'All', value: '' as IncentiveState | ''},
        ...Object.values(IncentiveState).map(state => ({
            label: incentiveStateLabel[state],
            value: state as IncentiveState | '',
        })),
    ]), []);

    const structureItems = useMemo(() => ([
        {label: 'All structures', value: '' as IncentiveStructure | ''},
        ...Object.values(IncentiveStructure).map(structure => ({
            label: incentiveStructureLabel[structure],
            value: structure,
        })),
    ]), []);

    const typeItems = useMemo(() => ([
        {label: 'All types', value: '' as IncentiveType | ''},
        ...Object.values(IncentiveType).map(type => ({
            label: incentiveTypeLabel[type],
            value: type,
        })),
    ]), []);

    return (
        <div className={style.incentives}>
            <div className='page-header-sticky'>
                <div className='page-header-breadcrumbs'>
                    <Breadcrumbs/>
                </div>
                <Title
                    className='page-header-title'
                    toolbar={
                        <>
                            {ability.canCreate() && (
                                <Button
                                    intent={Intent.PRIMARY}
                                    leftIcon={<i className='fa fa-plus'/>}
                                    disabled={isBusy}
                                    onClick={() => createIncentiveModal.show()}
                                    responsive
                                >
                                    Create
                                </Button>
                            )}
                            <Button
                                intent={Intent.SUCCESS}
                                leftIcon={<i className='fa fa-refresh'/>}
                                disabled={isBusy}
                                onClick={() => incentivesQuery.refetch()}
                                responsive
                            >
                                Reload
                            </Button>
                        </>
                    }
                >
                    Expenses
                </Title>
            </div>
            <BusinessIncentivesNavigation/>
            <div className='layout-content-box'>
                <form className={style.filter} onSubmit={event => event.preventDefault()}>
                    <Input
                        value={filter.search}
                        onChange={value => setFilter(prev => ({...prev, search: value}))}
                        placeholder='Search by id, title, description or scope'
                        disabled={isBusy}
                        rightIcon={<i className='fa fa-search input-search-icon'/>}
                    />
                    <ToggleGroup<IncentiveState | ''>
                        value={filter.state}
                        items={stateToggleItems}
                        onChange={value => setFilter(prev => ({...prev, state: value}))}
                        disabled={isBusy}
                        ariaLabel='Filter by state'
                    />
                    <SelectInput<IncentiveType | ''>
                        value={filter.incentiveType}
                        items={typeItems}
                        onChange={value => setFilter(prev => ({...prev, incentiveType: value}))}
                        disabled={isBusy}
                        placeholder='Type'
                    />
                    <SelectInput<IncentiveStructure | ''>
                        value={filter.structure}
                        items={structureItems}
                        onChange={value => setFilter(prev => ({...prev, structure: value}))}
                        disabled={isBusy}
                        placeholder='Structure'
                    />
                    <DateRangePickerInput
                        className={style.periodFilter}
                        dateRange={{
                            startDate: parseIsoDate(filter.periodStart),
                            endDate: parseIsoDate(filter.periodEnd),
                        }}
                        allowPartial
                        allowFutureDates
                        dateFormat='yyyy-MM-dd'
                        placeholder='Period'
                        fullWidth
                        disabled={isBusy}
                        onChange={period => setFilter(prev => ({
                            ...prev,
                            periodStart: toIsoDate(period.startDate),
                            periodEnd: toIsoDate(period.endDate),
                        }))}
                    />
                    <Button
                        disabled={isBusy}
                        onClick={() => setFilter({
                            search: '',
                            state: '',
                            incentiveType: '',
                            structure: '',
                            periodStart: '',
                            periodEnd: '',
                        })}
                    >
                        Reset
                    </Button>
                </form>

                <TableSort<IBusinessIncentive>
                    records={filteredIncentives}
                    header
                    defaultSort={{
                        sort: sortByNumber,
                        field: 'id',
                        direction: TableSortDirection.DESC,
                    }}
                    columns={[
                        {
                            header: 'ID',
                            field: 'id',
                            sort: sortByNumber,
                            small: true,
                            cell: item => toDisplayText(item.id),
                        },
                        {
                            header: 'Title',
                            field: 'name',
                            sort: sortByString,
                            headerAttrs: {className: style.nameCell},
                            cellAttrs: {className: style.nameCell},
                            cellWrap: 'break-word',
                            cell: item => {
                                const id = toDisplayNumber(item.id);
                                const nameValue = toDisplayText(item.name);

                                return (
                                    <div className={style.nameStack}>
                                        <div className={style.nameMain}>
                                            {id === null ? (
                                                nameValue
                                            ) : (
                                                <NavLink
                                                    to={businessIncentivesLinks.businessIncentive(id)}
                                                >
                                                    {nameValue}
                                                </NavLink>
                                            )}
                                        </div>
                                        <div className={style.nameMeta}>{formatNameMeta(item.description)}</div>
                                    </div>
                                );
                            },
                        },
                        {
                            header: 'Type',
                            field: 'incentiveType',
                            sort: sortByString,
                            cell: item => {
                                const incentiveType = toDisplayText(item.incentiveType, '') as IncentiveType;
                                return incentiveTypeLabel[incentiveType] ?? toDisplayText(item.incentiveType);
                            },
                        },
                        {
                            header: 'Structure',
                            field: 'structure',
                            sort: sortByString,
                            cell: item => {
                                const structure = toDisplayText(item.structure, '') as IncentiveStructure;
                                return incentiveStructureLabel[structure] ?? toDisplayText(item.structure);
                            },
                        },
                        {
                            header: 'Tier',
                            field: 'currentTier',
                            sort: sortByString,
                            cell: item => {
                                const tiers = getTierRows(item);
                                return (
                                    <div className={style.tierListCompact}>
                                        {tiers.map(tier => (
                                            <span
                                                key={tier.key}
                                                className={[
                                                    style.tierItemCompact,
                                                    tier.active ? style.tierItemActive : style.tierItemInactive,
                                                ].join(' ')}
                                                title={tier.label}
                                            >
                                                {tier.label}
                                            </span>
                                        ))}
                                    </div>
                                );
                            },
                        },
                        {
                            header: 'Period',
                            field: 'startDate',
                            sort: sortByString,
                            cellAttrs: {className: style.periodCell},
                            cell: item => (
                                <div className={style.periodStack}>
                                    <div>{toDisplayText(item.startDate)}</div>
                                    <div>{toDisplayText(item.endDate)}</div>
                                </div>
                            ),
                        },
                        {
                            header: 'Scope',
                            field: 'scope',
                            cellAttrs: {
                                className: style.scopeCell,
                            },
                            cell: item => {
                                const scopePreview = formatScopePreview(item.scope);
                                return (
                                    <div className={style.scopePreview}>
                                        <Copy text={scopePreview}>{scopePreview}</Copy>
                                    </div>
                                );
                            },
                        },
                        {
                            header: 'State',
                            field: 'state',
                            sort: sortByString,
                            cell: item => {
                                const state = toDisplayText(item.state, '') as IncentiveState;
                                const stateLabel = incentiveStateLabel[state] ?? toDisplayText(item.state);
                                return stateLabel;
                            },
                        },
                        {
                            short: true,
                            cell: item => (
                                <TableRowActions>
                                    {ability.canEdit() && (
                                        <Button
                                            small
                                            variant='menu'
                                            onClick={() => editIncentiveModal.show({incentive: item})}
                                            disabled={isBusy || toDisplayText(item.state, '') === IncentiveState.CLOSED}
                                            leftIcon={<i className='fa fa-edit'/>}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                    {ability.canDelete() && (
                                        <Button
                                            small
                                            variant='menu'
                                            intent={Intent.DANGER}
                                            onClick={() => handleDelete(item)}
                                            disabled={isBusy}
                                            leftIcon={<i className='fa fa-trash'/>}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </TableRowActions>
                            ),
                        },
                    ]}
                    placeholder={incentivesQuery.isPending ? <Progress/> : undefined}
                />
            </div>
        </div>
    );
};
